import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import OAuthCallback from './pages/OAuthCallback';
import Dashboard from './pages/Dashboard';
import NotInDatabasePage from './pages/NotInDatabasePage';
import SessionExpiredModal from './components/SessionExpiredModal';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const authSyncAttempted = useRef(false);

  const checkAuth = () => {
    let authStatus = localStorage.getItem('isAuthenticated');
    let userData = localStorage.getItem('user');
    
    // Fallback to cookies if localStorage is missing
    if (!authStatus || !userData) {
      const cookieMap = Object.fromEntries(
        document.cookie.split('; ').filter(Boolean).map(part => {
          const sepIndex = part.indexOf('=');
          return [part.substring(0, sepIndex), part.substring(sepIndex + 1)];
        })
      );
      
      if (cookieMap.symbolica_auth === 'true' && cookieMap.symbolica_user) {
        try {
          userData = atob(cookieMap.symbolica_user);
          authStatus = 'true';
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('user', userData);
        } catch (error) {
          console.error('Error decoding cookie data:', error);
        }
      }
    }

    if ((authStatus === 'true' || authStatus === 'partial') && userData) {
      setIsAuthenticated(authStatus);
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authData = params.get('data');
    if (authData) {
      try {
        const payload = JSON.parse(atob(authData));
        if (payload.success && payload.user) {
          localStorage.setItem('user', JSON.stringify(payload.user));
          localStorage.setItem('isAuthenticated', 'true');
        } else if (payload.error === 'not_in_database' && payload.user) {
          localStorage.setItem('user', JSON.stringify(payload.user));
          localStorage.setItem('isAuthenticated', 'partial');
        }
      } catch (e) {
        console.error('Error processing auth callback data:', e);
      }
      window.history.replaceState({}, '', '/');
    }
    checkAuth();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => setShowSessionExpired(true);
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  // Additional effect to handle auth state sync for dashboard route
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === '/dashboard' && !authSyncAttempted.current) {
      // If we're on dashboard but don't have user state, double-check localStorage
      const authStatus = localStorage.getItem('isAuthenticated');
      const userData = localStorage.getItem('user');
      
      if ((authStatus === 'true' || authStatus === 'partial') && userData && (!isAuthenticated || !user)) {
        authSyncAttempted.current = true;
        // Force immediate state update
        setIsAuthenticated(authStatus);
        try {
          const userObj = JSON.parse(userData);
          setUser(userObj);
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    }
  }, [isAuthenticated, user]);

  const handleAuthComplete = () => {
    // Force immediate re-check of auth state and wait for React to update
    return new Promise((resolve) => {
      // First update the state
      const authStatus = localStorage.getItem('isAuthenticated');
      const userData = localStorage.getItem('user');
      
      if ((authStatus === 'true' || authStatus === 'partial') && userData) {
        setIsAuthenticated(authStatus);
        try {
          const userObj = JSON.parse(userData);
          setUser(userObj);
          // Use setTimeout to ensure React has processed the state updates
          setTimeout(() => {
            resolve();
          }, 150); // Increased timeout to ensure state update completion
        } catch (error) {
          console.error('Error parsing user data:', error);
          setUser(null);
          resolve();
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        resolve();
      }
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('originalAdminUser'); // Clean up view-as data
    document.cookie = 'symbolica_auth=; Max-Age=0; path=/';
    document.cookie = 'symbolica_user=; Max-Age=0; path=/';
    setIsAuthenticated(false);
    setUser(null);
  };



  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <SessionExpiredModal
        isOpen={showSessionExpired}
        onClose={() => setShowSessionExpired(false)}
      />
      <div className="App">
        <Routes>
            <Route 
              path="/" 
              element={
                isAuthenticated === 'partial' ? 
                  <Navigate to="/not-in-database" replace /> :
                  (isAuthenticated === 'true' ? <Navigate to="/dashboard#home" replace /> : <Landing />)
              } 
            />
            <Route 
              path="/auth/callback" 
              element={<OAuthCallback onAuthComplete={handleAuthComplete} />} 
            />
            <Route 
              path="/dashboard" 
              element={
                isAuthenticated === 'true' && user && !user.not_in_database ? (
                  <Dashboard user={user} onLogout={handleLogout} />
                ) : (
                  isAuthenticated === 'partial' || user?.not_in_database ? 
                    <Navigate to="/not-in-database" replace /> :
                    <Navigate to="/" replace />
                )
              } 
            />
            <Route 
              path="/not-in-database" 
              element={
                user && (isAuthenticated === 'partial' || user.not_in_database) ? (
                  <NotInDatabasePage user={user} />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
